import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { UserCheck, ShieldCheck, AlertCircle } from 'lucide-react';
import API from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';

const KycPage = () => {
  const { user, refreshUser } = useAuth();

  const [kycForm, setKycForm] = useState({
    fullName: '',
    dob: '',
    address: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const formRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch current user when component mounts
    const load = async () => {
      setIsLoading(true);
      try {
        await refreshUser();
      } catch (err) {
        // refreshUser already logs errors
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [refreshUser]);

  // Debug log to inspect user shape and loading state
  useEffect(() => {
    // Remove this log in production if you want
    console.log('KycPage render - isLoading:', isLoading, 'user:', user);
  }, [isLoading, user]);

  const handleKycSubmit = async (e) => {
    e.preventDefault();

    // Client-side guard: avoid sending if already pending/approved
    if (user?.kyc?.status === 'pending') {
      toast('Your KYC is already pending review.');
      return;
    }
    if (user?.kyc?.status === 'approved') {
      toast('You are already verified.');
      return;
    }

    setIsSubmitting(true);

    try {
      await API.post('/user/kyc', kycForm);
      toast.success('KYC submitted! Awaiting admin review.');
      setKycForm({ fullName: '', dob: '', address: '' });
      setIsEditing(false); // clear edit mode so form hides
      // refresh central user data
      setIsLoading(true);
      await refreshUser();
    } catch (err) {
      console.error('KYC submit failed:', err?.response?.data || err.message);
      // Show server-sent message if available
      const message = err?.response?.data?.message || err?.response?.data?.error || 'Failed to submit KYC';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  if (isLoading) return <div className="text-center py-20">Loading...</div>;

  // Helper to read submitted details in either shape (details.* or top-level)
  const kycDetails = user?.kyc?.details || {
    fullName: user?.kyc?.fullName,
    dob: user?.kyc?.dob,
    address: user?.kyc?.address,
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 " style={{ paddingTop: 'calc(var(--navbar-height) + 3rem)', paddingBottom: 'calc(var(--bottomnav-height) + 3rem)' }}>
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
              <ShieldCheck className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-primary mb-4">KYC Verification</h1>
            <p className="text-xl text-gray-600">Verify your identity to unlock full access</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main KYC Card */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
                {/* Status Badge */}
                {user?.kyc?.status && (
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-2xl font-bold">Verification Status</h3>
                    <span
                      className={`px-6 py-2 text-lg font-bold rounded-full ${
                        user.kyc.status === 'approved'
                          ? 'bg-success/10 text-success'
                          : user.kyc.status === 'rejected'
                          ? 'bg-error/10 text-error'
                          : 'bg-warning/10 text-warning'
                      }`}
                    >
                      {user.kyc.status.charAt(0).toUpperCase() + user.kyc.status.slice(1)}
                    </span>
                  </div>
                )}

                {/* Approved - show visible summary so page isn't blank */}
                {user?.kyc?.status === 'approved' && (
                  <div className="text-center py-12">
                    <UserCheck className="w-24 h-24 text-success mx-auto mb-6" />
                    <p className="text-2xl font-bold text-success">You're Fully Verified!</p>

                    <div className="mt-4 text-gray-700">
                      {user?.name && <p className="text-lg font-medium">{user.name}</p>}

                      <p className="mt-2">
                        <strong>Verified on:</strong>{' '}
                        {user.kyc?.approvedAt
                          ? new Date(user.kyc.approvedAt).toLocaleDateString()
                          : user.kyc?.submittedAt
                          ? new Date(user.kyc.submittedAt).toLocaleDateString()
                          : 'N/A'}
                      </p>

                      <p className="mt-2">
                        <strong>Submitted details:</strong>{' '}
                        {kycDetails?.fullName || '—'} {kycDetails?.address ? `• ${kycDetails.address}` : ''}
                      </p>
                    </div>

                    <p className="text-gray-600 mt-4">Enjoy unlimited trading and withdrawals</p>
                  </div>
                )}

                {/* Rejected */}
                {user?.kyc?.status === 'rejected' && (
                  <div className="text-center py-8">
                    <AlertCircle className="w-20 h-20 text-error mx-auto mb-6" />
                    <p className="text-xl font-bold text-error mb-4">KYC Rejected</p>
                    <p className="text-gray-600 mb-6">Reason: {user.kyc.reason || 'Not specified'}</p>
                    <button
                      onClick={() => {
                        setKycForm({
                          fullName: user?.name || kycDetails?.fullName || '',
                          dob: kycDetails?.dob || '',
                          address: kycDetails?.address || '',
                        });
                        setIsEditing(true);
                        // scroll the form into view
                        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      className="bg-primary text-white px-8 py-3 rounded-lg font-bold hover:bg-primary/90"
                    >
                      Resubmit KYC
                    </button>
                  </div>
                )}

                {/* Pending */}
                {user?.kyc?.status === 'pending' && (
                  <div className="text-center py-12">
                    <div className="animate-spin w-16 h-16 border-4 border-primary border-t-transparent rounded-full mx-auto mb-6"></div>
                    <p className="text-xl font-bold text-warning">Under Review</p>
                    <p className="text-gray-600 mt-4">We'll notify you via email within 24 hours</p>
                  </div>
                )}

                {/* Form - show when editing or if no kyc exists */}
                {(isEditing || !user?.kyc?.status || user?.kyc?.status === 'none') && (
                  <form ref={formRef} onSubmit={handleKycSubmit} className="space-y-6">
                    <div>
                      <label className="block text-lg font-semibold mb-2">Full Name</label>
                      <input
                        type="text"
                        required
                        disabled={isSubmitting}
                        value={kycForm.fullName}
                        onChange={(e) => setKycForm({ ...kycForm, fullName: e.target.value })}
                        className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none text-lg"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-semibold mb-2">Date of Birth</label>
                      <input
                        type="date"
                        required
                        disabled={isSubmitting}
                        value={kycForm.dob}
                        onChange={(e) => setKycForm({ ...kycForm, dob: e.target.value })}
                        className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none text-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-semibold mb-2">Residential Address</label>
                      <textarea
                        rows={4}
                        required
                        disabled={isSubmitting}
                        value={kycForm.address}
                        onChange={(e) => setKycForm({ ...kycForm, address: e.target.value })}
                        className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:border-primary focus:outline-none text-lg resize-none"
                        placeholder="123 Main St, New York, USA"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary text-white py-5 rounded-xl font-bold text-xl hover:bg-primary/90 disabled:opacity-60 transition-all"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit for Verification'}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Info Sidebar */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-2xl border border-primary/20">
                <h3 className="text-xl font-bold mb-4">Why Verify?</h3>
                <ul className="space-y-4 text-gray-700">
                  <li className="flex items-start gap-3">
                    <UserCheck className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span>Unlock higher withdrawal limits</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span>Access advanced trading features</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <span>Required for regulatory compliance</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <p className="text-sm text-gray-600">
                  <strong>Processing Time:</strong> Usually within 24 hours<br />
                  <strong>Documents Required:</strong> Government ID + Proof of Address<br />
                  <strong>Support:</strong> support@swiftvault.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BottomNav />
    </>
  );
};

export default KycPage;